import {
  collection,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  setDoc,
  startAfter,
  where,
  addDoc,
} from 'firebase/firestore';

import { DEFAULT_PAGE_SIZE } from '../constants/pagination';
import { buildMintDocId, normalizeMinterDocs } from '../utils/mintOptions';
import { auth, db } from './client';
import { callFunction } from './functionsService';

export async function fetchCoin(uid) {
  const snapshot = await getDoc(doc(db, 'coins', uid));
  return snapshot.exists() ? snapshot.data() : null;
}

export async function fetchCoinWithOwner(uid) {
  const coin = await fetchCoin(uid);
  if (!coin) {
    return null;
  }

  if (coin.owner != null) {
    const ownerSnapshot = await getDoc(doc(db, 'users', coin.owner));
    if (ownerSnapshot.exists()) {
      coin.owner = ownerSnapshot.data();
    }
  }

  return coin;
}

export async function fetchProduct(uid) {
  const snapshot = await getDoc(doc(db, 'coin_product', uid));
  return snapshot.exists() ? snapshot.data() : null;
}

function mapCoinDocs(docs) {
  return docs.map((coinDoc) => ({
    ...coinDoc.data(),
    uid: coinDoc.id,
  }));
}

function mapProductDocs(docs) {
  return docs.map((productDoc) => ({
    ...productDoc.data(),
    uid: productDoc.id,
  }));
}

export async function fetchCoinProductsPage({
  pageSize = DEFAULT_PAGE_SIZE,
  startAfterDoc = null,
} = {}) {
  const constraints = [orderBy('bullion_type', 'desc'), orderBy('weight_oz')];

  if (startAfterDoc) {
    constraints.push(startAfter(startAfterDoc));
  }

  constraints.push(limit(pageSize + 1));

  const productsQuery = query(collection(db, 'coin_product'), ...constraints);
  const [snapshot, countSnapshot] = await Promise.all([
    getDocs(productsQuery),
    startAfterDoc
      ? Promise.resolve(null)
      : getCountFromServer(query(collection(db, 'coin_product'))),
  ]);
  const docs = snapshot.docs;
  const hasNextPage = docs.length > pageSize;
  const pageDocs = hasNextPage ? docs.slice(0, pageSize) : docs;

  return {
    products: mapProductDocs(pageDocs),
    hasNextPage,
    endCursor: pageDocs.length > 0 ? pageDocs[pageDocs.length - 1] : null,
    totalCount: countSnapshot ? countSnapshot.data().count : null,
  };
}

export async function fetchCoinProducts() {
  const result = await fetchCoinProductsPage({ pageSize: 500 });
  return result.products;
}

export async function countCoinsForOwner(ownerId) {
  const coinsQuery = query(collection(db, 'coins'), where('owner', '==', ownerId));
  const snapshot = await getCountFromServer(coinsQuery);
  return snapshot.data().count;
}

export async function fetchCoinsForOwner(ownerId, orderByField = 'weight_oz', max = 500) {
  const coinsQuery = query(
    collection(db, 'coins'),
    where('owner', '==', ownerId),
    orderBy(orderByField),
    limit(max)
  );
  const snapshot = await getDocs(coinsQuery);
  return enrichCoinsWithBuyback(mapCoinDocs(snapshot.docs));
}

export async function fetchCoinsPageForOwner(
  ownerId,
  { orderByField = 'weight_oz', pageSize = DEFAULT_PAGE_SIZE, startAfterDoc = null } = {}
) {
  const constraints = [where('owner', '==', ownerId), orderBy(orderByField)];

  if (startAfterDoc) {
    constraints.push(startAfter(startAfterDoc));
  }

  constraints.push(limit(pageSize + 1));

  const snapshot = await getDocs(query(collection(db, 'coins'), ...constraints));
  const docs = snapshot.docs;
  const hasNextPage = docs.length > pageSize;
  const pageDocs = hasNextPage ? docs.slice(0, pageSize) : docs;
  const coins = await enrichCoinsWithBuyback(mapCoinDocs(pageDocs));

  return {
    coins,
    hasNextPage,
    endCursor: pageDocs.length > 0 ? pageDocs[pageDocs.length - 1] : null,
  };
}

export async function enrichCoinsWithBuyback(coins) {
  const productIds = [
    ...new Set(
      coins
        .filter((coin) => coin.buyback == null && coin.coin_product)
        .map((coin) => coin.coin_product)
    ),
  ];

  const buybackByProductId = {};
  await Promise.all(
    productIds.map(async (productId) => {
      const product = await fetchProduct(productId);
      if (product) {
        buybackByProductId[productId] = product.buyback ?? 0;
      }
    })
  );

  return coins.map((coin) => ({
    ...coin,
    buyback: coin.buyback ?? buybackByProductId[coin.coin_product] ?? 0,
  }));
}

export async function fetchCoinTypes(coinType) {
  const snapshot = await getDocs(collection(db, coinType));
  return snapshot.docs.map((typeDoc) => ({
    ...typeDoc.data(),
    uid: typeDoc.id,
  }));
}

export async function fetchCoinMinters() {
  const snapshot = await getDocs(collection(db, 'coin_minter'));
  return normalizeMinterDocs(snapshot.docs);
}

export async function fetchMintNamesFromProducts() {
  const snapshot = await getDocs(collection(db, 'coin_product'));
  const names = new Set();

  snapshot.docs.forEach((productDoc) => {
    const mint = String(productDoc.data().mint || '').trim();
    if (mint) {
      names.add(mint);
    }
  });

  return [...names]
    .sort((left, right) => left.localeCompare(right))
    .map((name) => ({
      uid: buildMintDocId(name),
      name,
    }));
}

export async function createCoinMinter(name) {
  const trimmedName = String(name || '').trim();
  if (!trimmedName) {
    throw new Error('Mint name is required.');
  }

  const uid = buildMintDocId(trimmedName);

  await setDoc(doc(db, 'coin_minter', uid), { name: trimmedName }, { merge: true });
  return { uid, name: trimmedName };
}

function normalizeCoinProductData({
  name,
  bullion_purity,
  bullion_type,
  description,
  buyback,
  mint,
  image_front,
  premium,
  url,
  weight_oz,
}) {
  return {
    bullion_purity: parseFloat(bullion_purity) || 0,
    bullion_type: bullion_type ?? '',
    buyback: parseFloat(buyback) || 0,
    description: description ?? '',
    image_front: image_front ?? '',
    mint: mint ?? '',
    name: name ?? '',
    premium: parseFloat(premium) || 0,
    url: url ?? '',
    weight_oz: parseFloat(weight_oz) || 0,
  };
}

export async function updateCoinProductImageFront(uid, imageFront) {
  const trimmedUid = String(uid || '').trim();
  if (!trimmedUid) {
    throw new Error('Product ID is required to save the image.');
  }

  await setDoc(
    doc(db, 'coin_product', trimmedUid),
    { image_front: imageFront ?? '' },
    { merge: true }
  );
}

export async function createCoinProduct(
  uid,
  name,
  bullion_purity,
  bullion_type,
  description,
  buyback,
  mint,
  image_front,
  premium,
  url,
  weight_oz
) {
  await setDoc(
    doc(db, 'coin_product', uid),
    normalizeCoinProductData({
      name,
      bullion_purity,
      bullion_type,
      description,
      buyback,
      mint,
      image_front,
      premium,
      url,
      weight_oz,
    }),
    { merge: true }
  );
}

export const saveCoinProduct = createCoinProduct;

export async function addCoin(product, coin_product, mint_year, purchase_price, ownerId = null) {
  const owner = ownerId || auth.currentUser?.uid;
  if (!owner) {
    throw new Error('Sign in required to add a coin.');
  }

  if (!product || !coin_product || !mint_year || purchase_price === '') {
    return null;
  }

  const docRef = await addDoc(collection(db, 'coins'), {
    bullion_purity: product.bullion_purity,
    bullion_type: product.bullion_type,
    cash_value: 0,
    coin_product,
    description: product.description,
    image_front: product.image_front,
    mint: product.mint,
    mint_year,
    nfc_id: '',
    owner,
    buyback: product.buyback ?? 0,
    premium: product.premium,
    printed: false,
    purchase_price: parseFloat(purchase_price),
    url: product.url,
    weight_oz: product.weight_oz,
  });

  return docRef.id;
}

export async function lookupUserByPhone(phoneNumber) {
  return callFunction('lookupUserByPhone', { phoneNumber });
}

export async function transferCoin(coinId, newOwnerId) {
  return callFunction('transferCoin', { coinId, newOwnerId });
}

export async function fetchUserByPhone(phoneNumber) {
  const result = await lookupUserByPhone(phoneNumber);
  return [{ uid: result.uid, phoneNumber: result.phoneNumber }];
}

export function coinDocRef(uid) {
  return doc(db, 'coins', uid);
}
